<script lang="ts">
/**
 * Multi-line text display row for detail views.
 *
 * Like `DetailRow` but stacks label above value vertically instead
 * of side-by-side, for longer text content that needs to wrap.
 * Preserves line breaks in the value via `whitespace-pre-line`.
 *
 * @example
 * ```vue
 * <SbList bordered separator>
 *   <TextBlockRow label="Description" :value="item.description" />
 *   <TextBlockRow label="Condition" :value="item.condition" />
 * </SbList>
 * ```
 */
export interface TextBlockRowProps {
  /** The field label. */
  label: string
  /** The text content. Shows "Not set" when absent. */
  value?: string | null
}
</script>

<script setup lang="ts">
import { SbListItem, SbListSection, SbListLabel } from "@unio/saltbox-ui"

defineProps<TextBlockRowProps>()
</script>

<template>
  <SbListItem>
    <SbListSection>
      <SbListLabel caption>{{ label }}</SbListLabel>
      <SbListLabel v-if="value">
        <p class="text-sm font-normal whitespace-pre-line">{{ value }}</p>
      </SbListLabel>
      <SbListLabel v-else caption>Not set</SbListLabel>
    </SbListSection>
  </SbListItem>
</template>
